import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogClose,
} from "@/components/shadcn/dialog";
import { UIButton } from "@/components/ui/button";
import { useDict } from "../../../app/[lang]/providers/DictionaryProvider";

interface PeriodDetail {
  month: number;
  invested: number;
  principalReturn: number;
  coupon: number;
  interest: number;
}

interface DialogEbaRetailDetailProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  funds: number;
  coupon: number | null;
  onClose: () => void;
}

export function DialogEbaRetailDetail({ open, onOpenChange, funds, coupon, onClose }: Omit<DialogEbaRetailDetailProps, 'scenario'>) {
  const dict = useDict();
  const n = 4;
  const months = [0, 3, 6, 9, 12];
  const P0 = funds || 0;
  const c = coupon ? coupon / 100 : 0;
  const m = 4;
  const r = c / m;
  const alpha = 0.10;

  // Helper to calculate scenario table
  function getScenarioTable(scenario: "lumpSum" | "reinvestPrincipal" | "reinvestPrincipalInterest") {
    let periods: PeriodDetail[] = [];
    let totalInterest = 0;
    if (scenario === "lumpSum") {
      let Ik = P0;
      periods.push({ month: 0, invested: Ik, principalReturn: 0, coupon: coupon || 0, interest: 0 });
      for (let k = 0; k < n; k++) {
        const Bk = Math.round(r * Ik);
        const PRk = Math.round(alpha * Ik);
        totalInterest += Bk;
        periods.push({
          month: months[k + 1],
          invested: Math.round(Ik),
          principalReturn: PRk,
          coupon: coupon || 0,
          interest: Bk,
        });
        Ik = Ik - PRk;
      }
    } else if (scenario === "reinvestPrincipal") {
      for (let k = 0; k <= n; k++) {
        periods.push({
          month: months[k],
          invested: P0,
          principalReturn: Math.round(alpha * P0),
          coupon: coupon || 0,
          interest: Math.round(r * P0),
        });
        if (k > 0) totalInterest += Math.round(r * P0);
      }
      periods = periods.slice(1);
    } else if (scenario === "reinvestPrincipalInterest") {
      let Ik = P0;
      periods.push({ month: 0, invested: Ik, principalReturn: 0, coupon: coupon || 0, interest: 0 });
      for (let k = 0; k < n; k++) {
        const Bk = Math.round(r * Ik);
        const PRk = Math.round(alpha * Ik);
        totalInterest += Bk;
        periods.push({
          month: months[k + 1],
          invested: Math.round(Ik),
          principalReturn: PRk,
          coupon: coupon || 0,
          interest: Bk,
        });
        Ik = Ik + Bk;
      }
      periods = periods.slice(1);
    }
    const annualReturn = P0 > 0 ? ((totalInterest / P0) * 100).toFixed(2) + " %" : "-";
    return { periods, totalInterest, annualReturn };
  }

  const scenarios = [
    {
      key: "lumpSum",
      title: dict.ebaRetail.lumpSumHeader,
    },
    {
      key: "reinvestPrincipal",
      title: dict.ebaRetail.reinvestPokokHeader,
    },
    {
      key: "reinvestPrincipalInterest",
      title: dict.ebaRetail.reinvestPokokBungaHeader,
    },
  ] as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="fixed top-10 left-1/2 -translate-x-1/2 translate-y-0
          w-full max-w-6xl min-w-4xl max-h-[80vh] overflow-y-auto
          bg-background border shadow-lg
          p-6" showCloseButton>
        <DialogHeader>
          <DialogTitle>{dict.ebaRetail.calculationResultTitle}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-8">
          {scenarios.map(s => {
            const { periods, totalInterest, annualReturn } = getScenarioTable(s.key);
            return (
              <div key={s.key}>
                <h3 className="font-bold text-lg mb-2 text-primary-800">{s.title}</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border">
                    <thead>
                      <tr className="bg-primary-700 text-white">
                        <th className="p-2">{dict.ebaRetail.periodMonth}</th>
                        <th className="p-2">{dict.ebaRetail.invested}</th>
                        <th className="p-2">{dict.ebaRetail.principalReturnAssumption}</th>
                        <th className="p-2">{dict.ebaRetail.principalReturn}</th>
                        <th className="p-2">{dict.ebaRetail.coupon}</th>
                        <th className="p-2">{dict.ebaRetail.interestPerPeriod}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periods.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          <td className="p-2 text-center">{row.month}</td>
                          <td className="p-2 text-right">{`Rp. ${row.invested.toLocaleString("id-ID")}`}</td>
                          <td className="p-2 text-center">10%</td>
                          <td className="p-2 text-right">{row.month === 0 ? "" : `Rp. ${row.principalReturn.toLocaleString("id-ID")}`}</td>
                          <td className="p-2 text-center">{row.coupon.toFixed(2)}%</td>
                          <td className="p-2 text-right">{row.month === 0 ? "" : `Rp. ${row.interest.toLocaleString("id-ID")}`}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-200 font-semibold">
                        <td className="p-2" colSpan={5}>{dict.ebaRetail.totalBunga}</td>
                        <td className="p-2 text-right">{`Rp. ${totalInterest.toLocaleString("id-ID")}`}</td>
                      </tr>
                      <tr className="bg-gray-100 font-semibold">
                        <td className="p-2" colSpan={5}>{dict.ebaRetail.annualReturn}</td>
                        <td className="p-2 text-right">{annualReturn}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 text-xs text-muted-foreground">
          <div className="font-semibold mb-1">{dict.ebaRetail.disclaimerTitle}</div>
          <ul className="list-disc pl-5 space-y-1">
            {(() => {
              const raw = dict.ebaRetail.disclaimerList;
              let arr: string[] = [];
              if (Array.isArray(raw)) arr = raw;
              else if (typeof raw === 'string') arr = raw.split('\n');
              else arr = [
                "Dana untuk diinvestasikan bergantung harga harian EBA",
                "Jumlah pokok yang keluar setiap 3 bulan bergantung pada penerimaan pool EBA SP",
                "Reinvestasi dilakukan pada saat tanggal pembayaran pokok dan bunga",
                "Bunga yang didapatkan belum termasuk pajak",
                "Meskipun tabel hanya menjelaskan untuk periode 1 tahun, namun investasi di EBA Ritel dapat dilakukan selama kurang ataupun lebih dari satu tahun"
              ];
              return arr.map((item: string, idx: number) => <li key={idx}>{item}</li>);
            })()}
          </ul>
        </div>
        <DialogFooter className="flex justify-end w-full mt-4">
          <DialogClose asChild>
            <UIButton variant="default" onClick={onClose}>{dict.ebaRetail.close}</UIButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
